import { endOfDay, isSameDay, startOfDay } from 'date-fns';
import React, { useMemo } from 'react';
import { TreeTaskCard } from 'shared/components/connected/task-card';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { halfHour } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { ID } from 'shared/types/id';
import { DayPartSections } from 'shared/types/page-sections';
import { Task } from 'shared/types/task';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getTaskPlanning } from 'shared/utils/get-task-planning';
import { createPlannedOccurrenceForDate } from 'shared/utils/schedules';

import { useDayPartTree } from '../../hooks/use-day-part-tree';
import { GroupedListProps } from '../../types';
import { DayPartHeader } from '../day-part-header';

const taskTreeItemTypes = [TreeItemTypes.Task];

const getDayPartStartTime = (dayPart: DayPartSections): number | null => {
  switch (dayPart) {
    case DayPartSections.AllDay:
      return null;
    case DayPartSections.Morning:
      return 9 * 60 * 60 * 1000; // 9am
    case DayPartSections.Afternoon:
      return 12 * 60 * 60 * 1000; // 12pm
    case DayPartSections.Evening:
      return 18 * 60 * 60 * 1000; // 6pm
    case DayPartSections.Overdue:
      return null;
    default:
      return null;
  }
};

export const DayPartGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useDayPartTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
  });

  const oldTreeData = useMemo(() => structuredClone(treeData), [treeData]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: Task[] = [];
    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];

    // Process each task to detect section changes
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }

        const task = tasksMap[item.id];
        const taskToUpdate = structuredClone(task);

        // Find which section this task is now in
        const newSectionItem = updatedItems.find((section) =>
          section.children?.includes(item.id),
        );
        const oldSectionItem = Object.values(oldTreeData).find((section) =>
          section.children?.includes(item.id),
        );

        if (!newSectionItem || !oldSectionItem) {
          return;
        }

        const newSectionId = newSectionItem.id as DayPartSections;
        const oldSectionId = oldSectionItem.id as DayPartSections;

        // Skip if task hasn't moved between sections
        if (newSectionId === oldSectionId) {
          return;
        }

        // Handle overdue section - tasks cannot be moved into overdue
        if (newSectionId === DayPartSections.Overdue) {
          return;
        }

        // Get the new timeslot startTime for the dayPart
        const newStartTime = getDayPartStartTime(newSectionId);
        const newTimeslot = {
          startTime: newStartTime,
          duration: task.timeslot?.duration ?? halfHour,
        };

        // Handle scheduled tasks - create plannedOccurrence
        if (task.schedules?.length) {
          // Get the first upcoming/overdue date for the task
          const planning = getTaskPlanning(task, {
            startDate: startOfDay(today),
            endDate: endOfDay(today),
            weekStartsOn,
          });
          const firstOccurrence = planning[0];
          const targetDate = firstOccurrence?.date ?? today;

          const schedule = getActiveSchedule(task.schedules);
          const dayPlannedOccurrence = schedule?.plannedOccurrences?.find(
            ({ date }) => isSameDay(date, targetDate),
          );

          newTimeslot.duration =
            dayPlannedOccurrence?.timeslot?.duration ?? halfHour;

          taskToUpdate.schedules = createPlannedOccurrenceForDate(
            task.schedules,
            targetDate,
            {
              timeslot: newTimeslot,
              originalDate: dayPlannedOccurrence?.date ?? targetDate,
              weekStartsOn,
            },
          );
          updatedDocs.push(taskToUpdate);
          return;
        }

        // For other task types without schedules, update timeslot
        taskToUpdate.timeslot =
          newStartTime || newTimeslot.duration ? newTimeslot : null;
        updatedDocs.push(taskToUpdate);
      }

      // Collect section updates for custom order
      if (item.type === TreeItemTypes.DayPart) {
        updatedSections.push({
          id: item.id,
          taskSorting: item.children ?? [],
        });
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  const renderItem: React.FC<RenderItemProps> = ({
    item,
    itemInstance,
    tree,
  }) => {
    const type = item.type;

    // Task
    if (type === TreeItemTypes.Task) {
      return tasksMap[item.sourceId] ? (
        <TreeTaskCard
          task={tasksMap[item.sourceId]}
          tree={tree}
          item={itemInstance}
          tasks={tasks}
        />
      ) : null;
    }

    // DayPart
    if (type === TreeItemTypes.DayPart) {
      return <DayPartHeader item={itemInstance} />;
    }

    return null;
  };

  return (
    <VirtualizedTreeList
      rootItemId={rootId}
      items={treeData}
      renderItem={renderItem}
      scrollElement={scrollElement}
      multiselectTypes={taskTreeItemTypes}
      onReorder={_onReorder}
      collapsedItems={collapsedSections}
      onCollapse={onSectionCollapse}
    />
  );
};
